import { PortfolioCentralProductOverviewEntry } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { lt, useFeatureFlag } from '@mews/framework';
import { Divider, DropDownButton, DropdownItem, IconName } from '@optimus-web/core';
import { useCentralProductForm } from '../../../hooks/useCentralProductForm';
import { useDeleteCentralProductDialog } from '../../../hooks/useDeleteCentralProductDialog';
import { useHandleAssignProperty } from '../../../hooks/useHandleAssignProperty';
import { AddProductToPropertyDialog } from '../../_shared/AddProductToPropertyDialog';
import { CentralProductForm } from '../../CentralProductForm/CentralProductForm';
import { useCentralProductSidePanelContext } from '../../CentralProductSidePanel/CentralProductSidePanelContext';
import { ProductSidePanelTabValues } from './types';

interface RowActionCellProps {
    id: PortfolioCentralProductOverviewEntry['id'];
    propertyIds: PortfolioCentralProductOverviewEntry['propertyIds'];
}

export const RowActionCell = ({ id, propertyIds }: RowActionCellProps) => {
    const { open } = useCentralProductSidePanelContext();

    const centralProductsEditEnabled = useFeatureFlag(FeatureFlag.centralProductsEditEnabled);

    const {
        states: updateCentralProductStates,
        handlers: updateCentralProductHandlers,
        isPending,
        formErrors,
        handleSubmit,
    } = useCentralProductForm({ centralProductId: id });

    const {
        handleAddToProperty,
        handleConfirm,
        handlers: assignHandlers,
        renderConfigureProductDialog,
        states: assignStates,
    } = useHandleAssignProperty(id);

    const { confirmationDialog, openConfirmationDialog } = useDeleteCentralProductDialog({
        centralProductIds: [id],
        propertyIdsLength: propertyIds.length,
    });

    return (
        <>
            <DropDownButton icon={IconName.More} size="small" variant="ghost">
                <DropdownItem
                    label={lt('ViewDetails')}
                    onClick={() => open(id, ProductSidePanelTabValues.Details, propertyIds)}
                />
                {centralProductsEditEnabled && (
                    <DropdownItem label={lt('EditDetails')} onClick={updateCentralProductHandlers.handleOpen} />
                )}
                <DropdownItem label={lt('AddToProperty')} onClick={handleAddToProperty} />
                <Divider />
                <DropdownItem label={lt('DeleteProduct')} variant="danger" onClick={openConfirmationDialog} />
            </DropDownButton>
            {/* Edit central product */}
            {updateCentralProductStates.isDialogOpened && (
                <CentralProductForm
                    isLoading={isPending}
                    active={updateCentralProductStates.isDialogOpened}
                    formErrors={formErrors}
                    onCancel={updateCentralProductHandlers.handleCancel}
                    onSubmit={handleSubmit}
                    centralProductId={id}
                />
            )}
            {/* Add property to central product */}
            {assignStates.isDialogOpened && (
                <AddProductToPropertyDialog
                    onConfirm={handleConfirm}
                    onCancel={assignHandlers.handleCancel}
                    propertyIds={propertyIds}
                />
            )}
            {/* Configure properties to central product */}
            {renderConfigureProductDialog()}
            {/* Delete central product */}
            {confirmationDialog}
        </>
    );
};
